import type { EditedOcrDrugRow } from '@/pages/prescriptions/prescriptionOcrUtils';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';

interface EditedOcrComparisonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultRows: EditedOcrDrugRow[];
  geminiRows: EditedOcrDrugRow[];
  onApply: (source: 'default' | 'gemini') => void;
  applying?: boolean;
  /** 레거시 — 이번 요청 좌표 영역 미리보기 */
  previewUrl?: string | null;
}

const renderTable = (rows: EditedOcrDrugRow[]) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>#</TableHead>
        <TableHead>코드</TableHead>
        <TableHead>품명</TableHead>
        <TableHead>원외</TableHead>
        <TableHead>원내</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={5} className="text-center text-muted-foreground">
            결과 없음
          </TableCell>
        </TableRow>
      ) : (
        rows.map((row, idx) => (
          <TableRow key={`${row.drugCode ?? 'row'}-${idx}`}>
            <TableCell>{idx + 1}</TableCell>
            <TableCell>{row.drugCode ?? '—'}</TableCell>
            <TableCell>{row.drugName ?? '—'}</TableCell>
            <TableCell>{row.outHospitalQuantity}</TableCell>
            <TableCell>{row.inHospitalQuantity}</TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
);

interface ResultPanelProps {
  title: string;
  count: number;
  rows: EditedOcrDrugRow[];
  applyLabel: string;
  applyVariant?: 'default' | 'secondary';
  applying: boolean;
  onApply: () => void;
}

/** 서비스별 결과 패널 — 긴 의약품 목록은 패널 내부에서만 스크롤 */
const ResultPanel = ({
  title,
  count,
  rows,
  applyLabel,
  applyVariant = 'default',
  applying,
  onApply,
}: ResultPanelProps) => (
  <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 rounded-12 border border-border bg-card p-3">
    <div className="flex shrink-0 items-center justify-between gap-2">
      <h3 className="text-sm font-semibold">
        {title}{' '}
        <span className="font-normal text-muted-foreground">({count}건)</span>
      </h3>
      <Button
        size="sm"
        variant={applyVariant}
        disabled={rows.length === 0 || applying}
        onClick={onApply}
      >
        {applyLabel}
      </Button>
    </div>
    <div className="min-h-0 max-h-[min(52vh,480px)] flex-1 overflow-auto rounded-8 border border-border">
      {renderTable(rows)}
    </div>
  </section>
);

/**
 * Document AI · Gemini OCR 결과를 좌우로 비교하는 모달.
 * 각 서비스 의약품 목록은 독립 스크롤한다.
 */
export const EditedOcrComparisonModal = ({
  open,
  onOpenChange,
  defaultRows,
  geminiRows,
  onApply,
  applying = false,
  previewUrl,
}: EditedOcrComparisonModalProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="flex max-h-[90vh] max-w-6xl flex-col gap-4 overflow-hidden">
      <DialogHeader className="shrink-0">
        <DialogTitle>편집 OCR 비교</DialogTitle>
      </DialogHeader>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
        <p className="text-sm text-muted-foreground">
          Document AI와 Gemini 결과를 나란히 비교한 뒤, 반영할 결과를 선택해
          주세요.
        </p>

        {previewUrl ? (
          <div className="rounded-12 border border-border p-3">
            <p className="text-sm font-medium">이번 요청 좌표 영역 미리보기</p>
            <p className="mb-2 text-xs text-muted-foreground">
              좌표 수정 후 OCR을 요청한 영역입니다.
            </p>
            <div className="max-h-48 overflow-auto rounded-8 border border-border bg-muted/40 p-2">
              <img
                src={previewUrl}
                alt="요청 좌표 미리보기"
                className="mx-auto block h-auto max-h-44 w-auto max-w-full object-contain"
              />
            </div>
          </div>
        ) : null}

        <div className="grid min-h-0 grid-cols-1 gap-4 lg:grid-cols-2">
          <ResultPanel
            title="Document AI"
            count={defaultRows.length}
            rows={defaultRows}
            applyLabel="Document AI 적용"
            applying={applying}
            onApply={() => onApply('default')}
          />
          <ResultPanel
            title="Gemini"
            count={geminiRows.length}
            rows={geminiRows}
            applyLabel="Gemini 적용"
            applyVariant="secondary"
            applying={applying}
            onApply={() => onApply('gemini')}
          />
        </div>
      </div>

      <DialogFooter className="shrink-0">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          닫기
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
